import axiosInstance from "../config/axiosConfig";

export interface CallQualityScorecardRequest {
  startTime: string;
  endTime: string;
  comparisonStartTime?: string | null;
  comparisonEndTime?: string | null;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
}

export interface CallQualityScorecardDistribution {
  goodPercentage: number;
  poorPercentage: number;
  unclassifiedPercentage: number;
  totalCalls: number;
}

export interface CallQualityScorecardKpiCard {
  metricName: string;
  value: string;
  target: string | null;
  percentageChange: number | null;
  isTargetMet: boolean;
}

export interface CallQualityScorecardResponse {
  distribution: CallQualityScorecardDistribution;
  kpiCards: CallQualityScorecardKpiCard[];
  failureCallIds?: CallQualityFailureCallIds;
  callSetupIds?: string[];
  callDropIds?: string[];
}

export type CallQualityDistributionResponse = CallQualityScorecardDistribution;

export interface CallQualityMetricsData {
  totalCallsOffered: number;
  poorCallRate: number;
  setupFailureRate: number;
  dropFailureRate: number;
  averagePacketLoss: number;
  averageMOSScore: number;
  averageJitter: number;
  averageRoundTripTime: number;
}

export interface CallQualityMetricsResponse {
  metrics: CallQualityMetricsData;
  kpiCards: CallQualityScorecardKpiCard[];
  failureCallIds?: CallQualityFailureCallIds;
  callSetupIds?: string[];
  callDropIds?: string[];
}

export interface CallQualityFailureCallIds {
  setupFailedCallIds?: string[];
  dropFailedCallIds?: string[];
}

function buildScorecardRequestBody(params: CallQualityScorecardRequest): Record<string, unknown> {
  return {
    startTime: params.startTime,
    endTime: params.endTime,
    comparisonStartTime: params.comparisonStartTime ?? null,
    comparisonEndTime: params.comparisonEndTime ?? null,
    queueMicrosoftIds: params.queueMicrosoftIds ?? null,
    agentMicrosoftIds: params.agentMicrosoftIds ?? null,
  };
}

export async function fetchCallQualityScorecard(
  params: CallQualityScorecardRequest,
  options?: { signal?: AbortSignal }
): Promise<CallQualityScorecardResponse> {
  const res = await axiosInstance.post<CallQualityScorecardResponse>(
    "/call-quality/scorecard",
    buildScorecardRequestBody(params),
    { signal: options?.signal }
  );
  return res.data;
}

export async function fetchCallQualityDistribution(
  params: CallQualityScorecardRequest,
  options?: { signal?: AbortSignal }
): Promise<CallQualityDistributionResponse> {
  const res = await axiosInstance.post<CallQualityDistributionResponse>(
    "/call-quality/distribution",
    buildScorecardRequestBody(params),
    { signal: options?.signal }
  );
  return res.data;
}

export async function fetchCallQualityMetrics(
  params: CallQualityScorecardRequest,
  options?: { signal?: AbortSignal }
): Promise<CallQualityMetricsResponse> {
  const res = await axiosInstance.post<CallQualityMetricsResponse>(
    "/call-quality/metrics",
    buildScorecardRequestBody(params),
    { signal: options?.signal }
  );
  return res.data;
}

export interface SegmentStreamResponse {
  data?: SegmentStreamRecord[];
}

/** Path param is CallUniqueId (int); legacy callers may pass callId string until backend migrates. */
export async function fetchSegmentStream(
  callUniqueId: number | string,
  options?: { signal?: AbortSignal }
): Promise<SegmentStreamResponse> {
  const response = await axiosInstance.get<SegmentStreamResponse>(
    `/segment-stream/${encodeURIComponent(String(callUniqueId))}`,
    { signal: options?.signal }
  );
  return response.data;
}

export interface SegmentStreamDevice {
  [key: string]: unknown;
  deviceName?: string | null;
  cpuName?: string | null;
  os?: string | null;
}

export interface SegmentStreamNetwork {
  [key: string]: unknown;
  connectionType?: string | null;
  ipAddress?: string | null;
  wifiBand?: string | null;
  wifiRadioType?: string | null;
}

export interface SegmentStreamItem {
  [key: string]: unknown;
  streamId?: string;
  streamDirection?: "callerToCallee" | "calleeToCaller" | string;
  quality?: string | null;
}

export interface SegmentStreamRecord {
  caller?: string | null;
  callee?: string | null;
  callerHeader?: string | null;
  calleeHeader?: string | null;
  duration?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  failureType?: string | null;
  callerDevice?: SegmentStreamDevice | null;
  calleeDevice?: SegmentStreamDevice | null;
  callerNetWork?: SegmentStreamNetwork | null;
  calleeNetWork?: SegmentStreamNetwork | null;
  streams?: SegmentStreamItem[];
}

export async function fetchSegmentStreamByCallUniqueId(
  callUniqueId: number | string,
  options?: { signal?: AbortSignal }
): Promise<SegmentStreamRecord[]> {
  const res = await fetchSegmentStream(callUniqueId, options);
  const payload = res as { data?: SegmentStreamRecord[] } | SegmentStreamRecord[] | null | undefined;
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
}
